import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLink, File, Download, X, Video } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { preventDismissFromSiblingDialog } from "@/lib/dialog-guards";
import { useToast } from "@/hooks/use-toast";
import { InlineMediaPreview, isVideoMediaFile, isImageMediaFile } from "@/components/attachments/inline-media";
import DocumentUploadButton from "@/components/document-upload-button";

function isInlineMedia(att: Attachment): boolean {
  return att.type === "media" && (isVideoMediaFile(att) || isImageMediaFile(att));
}

type Attachment = {
  id: string;
  name: string;
  type: "media" | "drive" | "url";
  fileUrl?: string;
  webViewLink?: string;
  mimeType?: string;
  fileType?: string;
  url?: string;
};

function safeUrl(url: string | null | undefined): string {
  if (!url) return "#";
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "http:" || parsed.protocol === "https:") return url;
  } catch {}
  return "#";
}

/**
 * Opens an attachment in a new tab. Uploaded media files are stored as data:
 * URLs, which browsers refuse to open directly — convert them to a Blob URL
 * first so the document opens in a viewer tab the user can simply close.
 */
/** Mime types the browser can render in a tab; everything else must download. */
function isViewableMime(mime: string): boolean {
  return (
    mime.startsWith("image/") ||
    mime.startsWith("text/") ||
    mime.startsWith("audio/") ||
    mime.startsWith("video/") ||
    mime === "application/pdf" ||
    mime === "application/json"
  );
}

function dataUrlMime(dataUrl: string): string {
  const match = /^data:([^;,]+)/.exec(dataUrl);
  return match?.[1] ?? "";
}

const EXT_TO_MIME: Record<string, string> = {
  mp4: "video/mp4",
  webm: "video/webm",
  ogg: "video/ogg",
  mov: "video/quicktime",
  quicktime: "video/quicktime",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  "svg+xml": "image/svg+xml",
  bmp: "image/bmp",
  avif: "image/avif",
  pdf: "application/pdf",
  json: "application/json",
  txt: "text/plain",
  plain: "text/plain",
  mp3: "audio/mpeg",
  mpeg: "audio/mpeg",
  wav: "audio/wav",
};
/** Downloads a media attachment using its real file name as the save-as name. */
function downloadAttachment(attachment: Attachment) {
  if (!attachment.fileUrl) return;
  const a = document.createElement("a");
  a.href = attachment.fileUrl;
  a.download = attachment.name || "document";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function openAttachment(attachment: Attachment) {
  // Media records store either a data: URL (legacy base64) or a served
  // object-storage URL (same-origin path). Both are fetched and shown via a
  // blob URL so the viewer tab behaves identically for old and new records.
  if (attachment.type === "media" && attachment.fileUrl) {
    // Non-viewable types (Word, Excel, zip, ...) would force a download with a
    // random blob name — download them directly with the real file name instead.
    if (!isViewableMime(attachmentMime(attachment))) {
      downloadAttachment(attachment);
      return;
    }
    // Open the tab synchronously (inside the click's user activation) so popup
    // blockers allow it, then navigate it once the blob is ready. The blob has
    // an opaque origin, so keeping an opener reference is safe here.
    const viewer = window.open("about:blank", "_blank");
    if (!viewer) return; // popup blocked
    fetch(attachment.fileUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        viewer.location.href = blobUrl;
        // Release the blob only once the viewer tab is closed.
        const timer = window.setInterval(() => {
          if (viewer.closed) {
            window.clearInterval(timer);
            URL.revokeObjectURL(blobUrl);
          }
        }, 2_000);
      })
      .catch(() => {
        viewer.close(); // malformed data URL — nothing to show
      });
    return;
  }
  const raw = attachment.type === "url" ? attachment.url :
              attachment.type === "drive" ? attachment.webViewLink :
              attachment.fileUrl;
  const href = safeUrl(raw);
  if (href !== "#") window.open(href, "_blank", "noopener,noreferrer");
}

export function TaskAttachmentsDialog({ taskId, trigger }: { taskId: string; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  const { data: attachments = [], isLoading } = useQuery<Attachment[]>({
    queryKey: [`/api/tasks/${taskId}/attachments`],
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={(e) => e.stopPropagation()}>
        {trigger}
      </DialogTrigger>
      <DialogContent
        className="w-screen h-screen m-0 rounded-none overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onInteractOutside={preventDismissFromSiblingDialog}
      >
        <DialogHeader>
          <DialogTitle>Task Documents</DialogTitle>
          <DialogDescription>
            Click on a document to open it
          </DialogDescription>
        </DialogHeader>
        <div>
          <DocumentUploadButton entityType="task" entityId={taskId} />
        </div>
        <ScrollArea className="max-h-[400px]">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading documents...</p>
          ) : attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No documents attached to this task</p>
          ) : (
            <div className="space-y-2 py-2">
              {attachments.map((attachment) => {
                if (isInlineMedia(attachment)) {
                  return (
                    <div key={`${attachment.type}-${attachment.id}`} className="rounded-lg border overflow-hidden">
                      <div className="flex items-center justify-between p-3 border-b bg-muted/30">
                        <div className="flex items-center gap-2 min-w-0">
                          {isVideoMediaFile(attachment) ? (
                            <Video className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <File className="w-4 h-4 text-orange-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm truncate">{attachment.name}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAttachment(attachment)}
                          data-testid={`button-save-attachment-${attachment.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </div>
                      <InlineMediaPreview file={attachment} testId={`attachment-${attachment.id}`} />
                    </div>
                  );
                }
                return (
                  <div
                    key={`${attachment.type}-${attachment.id}`}
                    className="flex items-center gap-2 p-3 rounded-lg border hover:bg-accent hover:border-primary transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => openAttachment(attachment)}
                      className="flex-1 min-w-0 text-left flex items-center gap-3 cursor-pointer"
                      data-testid={`document-link-${attachment.id}`}
                    >
                      {attachment.type === "url" ? (
                        <ExternalLink className="h-5 w-5 text-blue-500 flex-shrink-0" />
                      ) : attachment.type === "drive" ? (
                        <File className="h-5 w-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <File className="h-5 w-5 text-orange-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{attachment.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {attachment.type === "media" ? "Media Library" : attachment.type === "drive" ? "Google Drive" : "External URL"}
                        </p>
                      </div>
                    </button>
                    {attachment.type === "media" && attachment.fileUrl ? (
                      <>
                        {isViewableMime(attachmentMime(attachment)) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAttachment(attachment)}
                            data-testid={`button-view-attachment-${attachment.id}`}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAttachment(attachment)}
                          data-testid={`button-save-attachment-${attachment.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                      </>
                    ) : (
                      <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export function TaskAttachmentsList({ taskId }: { taskId: string }) {
  const { toast } = useToast();

  const { data: attachments = [], isLoading } = useQuery<Attachment[]>({
    queryKey: [`/api/tasks/${taskId}/attachments`],
  });

  const deleteMutation = useMutation({
    mutationFn: async ({ id, type }: { id: string; type: "media" | "drive" | "url" }) => {
      const endpoint = type === "media"
        ? `/api/tasks/${taskId}/media/${id}`
        : type === "drive"
          ? `/api/tasks/${taskId}/drive/${id}`
          : `/api/tasks/${taskId}/url/${id}`;
      return apiRequest("DELETE", endpoint);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document removed successfully",
      });
      queryClient.invalidateQueries({ queryKey: [`/api/tasks/${taskId}/attachments`] });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove document",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading documents...</p>;
  }

  if (attachments.length === 0) {
    return <p className="text-sm text-muted-foreground">No documents attached</p>;
  }

  return (
    <div className="space-y-2" data-testid="task-attachments-list">
      {attachments.map((attachment) => (
        <Card key={`${attachment.type}-${attachment.id}`} className={isInlineMedia(attachment) ? "p-0 overflow-hidden" : "p-3"}>
          <div className={isInlineMedia(attachment) ? "flex items-center gap-3 p-3 border-b bg-muted/30" : "flex items-center gap-3"}>
            {attachment.type === "url" ? (
              <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            ) : (
              <File className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            )}
            <button
              type="button"
              className="flex-1 min-w-0 text-left cursor-pointer"
              onClick={() => openAttachment(attachment)}
              data-testid={`attachment-open-${attachment.id}`}
            >
              <p className="text-sm font-medium truncate">{attachment.name}</p>
              <p className="text-xs text-muted-foreground">
                {attachment.type === "media" ? "Media Library" : attachment.type === "drive" ? "Google Drive" : "External URL"}
              </p>
            </button>
            <div className="flex items-center gap-1">
              {attachment.type === "media" && attachment.fileUrl && (
                <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                  <a href={attachment.fileUrl} target="_blank" rel="noopener noreferrer" download={attachment.name}>
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {attachment.type === "drive" && attachment.webViewLink && (
                <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                  <a href={attachment.webViewLink} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4" />
                  </a>
                </Button>
              )}
              {attachment.type === "url" && attachment.url && (
                <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                  <a href={safeUrl(attachment.url)} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={() => deleteMutation.mutate({ id: attachment.id, type: attachment.type })}
                data-testid={`button-delete-attachment-${attachment.id}`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {isInlineMedia(attachment) && (
            <InlineMediaPreview file={attachment} testId={`attachment-${attachment.id}`} />
          )}
        </Card>
      ))}
    </div>
  );
}

/**
 * Best-effort mime type for a media attachment. Legacy records store base64
 * data URLs (mime embedded in the URL); newer records store served object
 * storage URLs, so the mime is inferred from fileType or the file extension.
 */
function attachmentMime(attachment: Attachment): string {
  if (attachment.fileUrl?.startsWith("data:")) {
    return dataUrlMime(attachment.fileUrl);
  }
  const sub = (
    attachment.fileType ||
    attachment.name.split(".").pop() ||
    ""
  ).toLowerCase();
  return EXT_TO_MIME[sub] ?? "";
}
